// Effects browser modal. Walks a Fisher-Yates permutation of every variant
// the catalog can produce — guaranteeing each call to Next shows a fresh,
// unseen composition until you've cycled the whole space.

import { createPipeline } from '@/core/pipeline';
import type { AppState } from '@/core/types';
import { INITIAL_STATE } from '@/core/types';
import { replaceState } from '@/core/state';
import { el, on } from '@/lib/dom';
import {
  buildEffect,
  effectLabel,
  getStream,
  totalEffects,
  type EffectStream,
} from '@/lib/effectsCatalog';

const PREVIEW_SIZE = 256;

let openInstance: { close: () => void } | null = null;

export function openEffectsBrowser(): void {
  if (openInstance) {
    openInstance.close();
    return;
  }

  const stream: EffectStream = getStream();
  const total = totalEffects();
  const dim = el('div', { class: 'modal-backdrop' });
  const dialog = el('div', { class: 'modal modal-effects', role: 'dialog', ariaLabel: 'Effects browser' });

  const head = el('div', { class: 'modal-head' }, [
    el('div', { class: 'modal-title' }, ['Discover effects']),
    el('div', { class: 'modal-subtitle' }, [
      `${total.toLocaleString()} unique compositions. Walk the catalog with ← → and never see the same one twice.`,
    ]),
  ]);

  const meta = el('div', { class: 'eff-meta' });
  const nameEl = el('span', { class: 'eff-name' });
  const tagsEl = el('span', { class: 'eff-tags mono' });
  const counterEl = el('span', { class: 'eff-counter mono' });
  meta.append(
    el('div', { class: 'eff-meta-left' }, [nameEl, el('br'), tagsEl]),
    counterEl,
  );

  const stage = el('div', { class: 'eff-stage' });
  const canvas = el('canvas', { class: 'eff-canvas' }) as HTMLCanvasElement;
  canvas.width = PREVIEW_SIZE;
  canvas.height = PREVIEW_SIZE;
  stage.appendChild(canvas);

  const prev = el('button', { class: 'btn', ariaLabel: 'Previous (←)' }, ['◀  Prev']);
  const random = el('button', { class: 'btn', ariaLabel: 'Random (R)' }, ['🎲  Random']);
  const apply = el('button', { class: 'btn primary', ariaLabel: 'Apply (Enter)' }, ['Apply to canvas']);
  const next = el('button', { class: 'btn', ariaLabel: 'Next (→)' }, ['Next  ▶']);
  const actions = el('div', { class: 'eff-actions' }, [prev, random, apply, next]);

  dialog.append(head, meta, stage, actions);
  dim.appendChild(dialog);
  document.body.appendChild(dim);

  // Render initial.
  let pendingIdx: number | null = stream.current();
  let inFlight = false;
  void renderCurrent();

  on(prev,   'click', () => goto(stream.prev()));
  on(next,   'click', () => goto(stream.next()));
  on(random, 'click', () => goto(stream.jumpRandom()));
  on(apply,  'click', () => applyCurrent());

  function goto(idx: number): void {
    pendingIdx = idx;
    void renderCurrent();
  }

  function applyCurrent(): void {
    const { layers } = buildEffect(stream.current());
    const next: AppState = {
      ...INITIAL_STATE,
      resolution: { w: PREVIEW_SIZE, h: PREVIEW_SIZE },
      layers,
      selectedLayerId: layers[0]?.id ?? null,
    };
    replaceState(next, true);
    close();
  }

  async function renderCurrent(): Promise<void> {
    if (inFlight) return;
    inFlight = true;
    try {
      while (pendingIdx !== null) {
        const idx = pendingIdx;
        pendingIdx = null;
        await render(idx);
      }
    } finally {
      inFlight = false;
    }
  }

  async function render(idx: number): Promise<void> {
    const { layers, meta: m } = buildEffect(idx);
    const sub: AppState = {
      ...INITIAL_STATE,
      resolution: { w: PREVIEW_SIZE, h: PREVIEW_SIZE },
      layers,
    };
    const pipeline = createPipeline();
    const result = await pipeline.render(sub);
    if (!result.image) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.putImageData(result.image, 0, 0);
    const lab = effectLabel(m);
    nameEl.textContent = lab.name;
    tagsEl.textContent = lab.tags;
    counterEl.textContent = `${(stream.cursorIndex() + 1).toLocaleString()} of ${total.toLocaleString()}`;
  }

  function close(): void {
    dim.remove();
    offKey();
    offClick();
    openInstance = null;
  }
  const offKey = on(window, 'keydown', (ev) => {
    const k = ev.key;
    if (k === 'Escape')        { close(); }
    else if (k === 'ArrowRight') { ev.preventDefault(); goto(stream.next()); }
    else if (k === 'ArrowLeft')  { ev.preventDefault(); goto(stream.prev()); }
    else if (k === 'r' || k === 'R') { ev.preventDefault(); goto(stream.jumpRandom()); }
    else if (k === 'Enter')      { ev.preventDefault(); applyCurrent(); }
  });
  const offClick = on(dim, 'click', (ev) => {
    if (ev.target === dim) close();
  });
  openInstance = { close };
}
